import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SESSION_IDLE_MAX_AGE_SEC } from "@lms/backend/lib/auth/session-policy";
import {
  broadcastPlatformSignOut,
  onOtherTabPlatformSignOut,
  readPlatformIdleLimit,
  readPlatformLastActivity,
  touchPlatformActivity,
} from "@/lib/platform-session-activity";
import { endPlatformSession } from "@/services/http/platform-session-refresh";
import { usePlatformAuthStore } from "@/stores/platformAuthStore";

const DEFAULT_IDLE_MS = SESSION_IDLE_MAX_AGE_SEC * 1000;
const MIN_IDLE_MS = 5 * 60_000;
const TICK_MS = 1_000;
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "pointermove",
  "wheel",
] as const;

export const PLATFORM_IDLE_LOGIN_PATH = "/platform/login?reason=idle";

/**
 * MT-1 AUTH-009 (AC-95, §3.9, R2-F5): the platform console's idle sign-out. It runs only while a
 * platform session is held, on its own clock (`platform-session-activity.ts`), and on idle ends
 * the platform session and opens `/platform/login`. It never reads or writes the school store or
 * the school clock; the server refuses an idle platform session on its own anyway.
 */
export function usePlatformIdleTimeout(): void {
  const isAuthenticated = usePlatformAuthStore((s) => s.isAuthenticated);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!isAuthenticated) return;
    let signingOut = false;
    const limitMs = readPlatformIdleLimit(MIN_IDLE_MS) ?? DEFAULT_IDLE_MS;

    const leave = () => navigateRef.current(PLATFORM_IDLE_LOGIN_PATH, { replace: true });

    const signOutHere = () => {
      if (signingOut) return;
      signingOut = true;
      void endPlatformSession().finally(() => {
        broadcastPlatformSignOut("idle");
        leave();
      });
    };

    if (readPlatformLastActivity() === null) touchPlatformActivity();
    const onActivity = () => touchPlatformActivity();
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }
    const stopListening = onOtherTabPlatformSignOut(() => {
      if (signingOut) return;
      signingOut = true;
      usePlatformAuthStore.getState().clearSession();
      leave();
    });

    const check = () => {
      const last = readPlatformLastActivity() ?? 0;
      if (Date.now() - last > limitMs) signOutHere();
    };
    check();
    const interval = window.setInterval(check, TICK_MS);
    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, onActivity);
      stopListening();
      window.clearInterval(interval);
    };
  }, [isAuthenticated]);
}
